import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import { TURNSTILE_SITE_KEY } from "@/constants/turnstile";
import { isDisposableEmail } from "@/constants/blockedEmailDomains";

const loginSchema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(1, "Password is required"),
});

const signupSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .max(100, "Name must be at most 100 characters long")
      .regex(/^[a-zA-ZÀ-ÿ\s]+$/, "Name must contain only letters"),
    email: z
      .string()
      .email("Invalid email")
      .refine(
        (email) => !isDisposableEmail(email),
        { message: "Temporary or disposable emails are not allowed. Please use a valid email address." }
      ),
    password: z.string().min(8, "Password must be at least 8 characters long"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const resetSchema = z.object({
  email: z.string().email("Invalid email"),
});

type LoginFormData = z.infer<typeof loginSchema>;
type SignupFormData = z.infer<typeof signupSchema>;
type ResetFormData = z.infer<typeof resetSchema>;

export function EmailAuthForm() {
  const { signInWithEmail, signUp, resetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [signupTurnstileToken, setSignupTurnstileToken] = useState<string | null>(null);
  const [loginTurnstileToken, setLoginTurnstileToken] = useState<string | null>(null);
  const [resetTurnstileToken, setResetTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const signupTurnstileRef = useRef<TurnstileInstance>(null);
  const loginTurnstileRef = useRef<TurnstileInstance>(null);
  const resetTurnstileRef = useRef<TurnstileInstance>(null);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const resetForm = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const onLogin = async (data: LoginFormData) => {
    if (!loginTurnstileToken) {
      toast.error("Please complete the verification challenge");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await signInWithEmail(data.email, data.password, loginTurnstileToken);
      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Incorrect email or password");
        } else if (error.message.includes("Email not confirmed")) {
          toast.error("Please confirm your email before logging in. Check your inbox.");
        } else {
          toast.error(error.message);
        }
        loginTurnstileRef.current?.reset();
        setLoginTurnstileToken(null);
      } else {
        toast.success("Login successful!");
      }
    } catch (error) {
      toast.error("Error logging in");
      loginTurnstileRef.current?.reset();
      setLoginTurnstileToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const onSignup = async (data: SignupFormData) => {
    if (!signupTurnstileToken) {
      toast.error("Please complete the verification challenge");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await signUp(data.email, data.password, data.name, signupTurnstileToken);
      if (error) {
        if (error.message.includes("already registered")) {
          toast.error("This email is already registered");
        } else {
          toast.error(error.message);
        }
        signupTurnstileRef.current?.reset();
        setSignupTurnstileToken(null);
      } else {
        // Show confirmation message instead of success
        setShowConfirmationMessage(true);
        signupForm.reset();
        signupTurnstileRef.current?.reset();
        setSignupTurnstileToken(null);
      }
    } catch (error) {
      toast.error("Error creating account");
      signupTurnstileRef.current?.reset();
      setSignupTurnstileToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const onResetPassword = async (data: ResetFormData) => {
    if (!resetTurnstileToken) {
      toast.error("Please complete the verification challenge");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await resetPassword(data.email, resetTurnstileToken);
      if (error) {
        toast.error(error.message);
        resetTurnstileRef.current?.reset();
        setResetTurnstileToken(null);
      } else {
        toast.success("Recovery email sent! Check your inbox.");
        setShowResetPassword(false);
        resetForm.reset();
        resetTurnstileRef.current?.reset();
        setResetTurnstileToken(null);
      }
    } catch (error) {
      toast.error("Error sending recovery email");
      resetTurnstileRef.current?.reset();
      setResetTurnstileToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupTurnstileSuccess = (token: string) => {
    setSignupTurnstileToken(token);
    setTurnstileError(false);
  };

  const handleLoginTurnstileSuccess = (token: string) => {
    setLoginTurnstileToken(token);
    setTurnstileError(false);
  };

  const handleResetTurnstileSuccess = (token: string) => {
    setResetTurnstileToken(token);
    setTurnstileError(false);
  };

  const handleTurnstileError = () => {
    setSignupTurnstileToken(null);
    setLoginTurnstileToken(null);
    setResetTurnstileToken(null);
    setTurnstileError(true);
    toast.error("Verification failed. Please try again.");
  };

  const handleSignupTurnstileExpire = () => {
    setSignupTurnstileToken(null);
  };

  const handleLoginTurnstileExpire = () => {
    setLoginTurnstileToken(null);
  };

  const handleResetTurnstileExpire = () => {
    setResetTurnstileToken(null);
  };

  // Show email confirmation message after successful signup
  if (showConfirmationMessage) {
    return (
      <div className="space-y-6 text-center py-8">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-semibold">Check Your Email</h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            We've sent a confirmation email to your inbox. Please click the link in the email to activate your account.
          </p>
        </div>
        <div className="pt-4">
          <p className="text-xs text-muted-foreground">
            Didn't receive the email? Check your spam folder or{" "}
            <button 
              onClick={() => {
                setShowConfirmationMessage(false);
                setActiveTab("signup");
              }}
              className="text-primary hover:underline"
            >
              try again
            </button>
          </p>
        </div>
        <Button
          variant="outline"
          className="w-full mt-4"
          onClick={() => {
            setShowConfirmationMessage(false);
            setActiveTab("login");
          }}
        >
          Back to Login
        </Button>
      </div>
    );
  }

  if (showResetPassword) {
    return (
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold">Recover Password</h3>
          <p className="text-sm text-muted-foreground">Enter your email to receive the recovery link</p>
        </div>
        <form onSubmit={resetForm.handleSubmit(onResetPassword)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-email">E-mail</Label>
            <Input
              id="reset-email"
              type="email"
              placeholder="you@email.com"
              {...resetForm.register("email")}
              disabled={isLoading}
            />
            {resetForm.formState.errors.email && (
              <p className="text-sm text-destructive">{resetForm.formState.errors.email.message}</p>
            )}
          </div>

          {/* Cloudflare Turnstile Widget for Reset Password */}
          <div className="flex justify-center">
            <Turnstile
              ref={resetTurnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={handleResetTurnstileSuccess}
              onError={handleTurnstileError}
              onExpire={handleResetTurnstileExpire}
              options={{
                theme: "dark",
                size: "normal",
              }}
            />
          </div>

          <Button
            type="submit"
            className="w-full glass-premium border-premium"
            disabled={isLoading || !resetTurnstileToken}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send Recovery Link
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full glass-premium border-premium"
            onClick={() => setShowResetPassword(false)}
            disabled={isLoading}
          >
            Back to Login
          </Button>
        </form>
      </div>
    );
  }

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="login">Log In</TabsTrigger>
        <TabsTrigger value="signup">Sign Up</TabsTrigger>
      </TabsList>

      <TabsContent value="login" forceMount className={`space-y-4 ${activeTab !== "login" ? "hidden" : ""}`}>
        <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-email">E-mail</Label>
            <Input
              id="login-email"
              type="email"
              placeholder="you@email.com"
              {...loginForm.register("email")}
              disabled={isLoading}
            />
            {loginForm.formState.errors.email && (
              <p className="text-sm text-destructive">{loginForm.formState.errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              type="password"
              placeholder="••••••••"
              {...loginForm.register("password")}
              disabled={isLoading}
            />
            {loginForm.formState.errors.password && (
              <p className="text-sm text-destructive">{loginForm.formState.errors.password.message}</p>
            )}
          </div>
          <Button
            type="button"
            variant="link"
            className="px-0 text-sm text-muted-foreground hover:text-foreground"
            onClick={() => setShowResetPassword(true)}
            disabled={isLoading}
          >
            Forgot your password?
          </Button>

          {/* Cloudflare Turnstile Widget for Login */}
          <div className="flex justify-center">
            <Turnstile
              ref={loginTurnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={handleLoginTurnstileSuccess}
              onError={handleTurnstileError}
              onExpire={handleLoginTurnstileExpire}
              options={{
                theme: "dark",
                size: "normal",
              }}
            />
          </div>

          <Button
            type="submit"
            className="w-full glass-premium border-premium"
            disabled={isLoading || !loginTurnstileToken}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Logging in...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>
      </TabsContent>

      <TabsContent value="signup" forceMount className={`space-y-4 ${activeTab !== "signup" ? "hidden" : ""}`}>
        <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signup-name">Full Name</Label>
            <Input
              id="signup-name"
              type="text"
              placeholder="Your full name"
              {...signupForm.register("name")}
              disabled={isLoading}
            />
            {signupForm.formState.errors.name && (
              <p className="text-sm text-destructive">{signupForm.formState.errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-email">E-mail</Label>
            <Input
              id="signup-email"
              type="email"
              placeholder="you@email.com"
              {...signupForm.register("email")}
              disabled={isLoading}
            />
            {signupForm.formState.errors.email && (
              <p className="text-sm text-destructive">{signupForm.formState.errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-password">Password</Label>
            <Input
              id="signup-password"
              type="password"
              placeholder="Minimum 8 characters"
              {...signupForm.register("password")}
              disabled={isLoading}
            />
            {signupForm.formState.errors.password && (
              <p className="text-sm text-destructive">{signupForm.formState.errors.password.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-confirm">Confirm Password</Label>
            <Input
              id="signup-confirm"
              type="password"
              placeholder="Enter the password again"
              {...signupForm.register("confirmPassword")}
              disabled={isLoading}
            />
            {signupForm.formState.errors.confirmPassword && (
              <p className="text-sm text-destructive">{signupForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>

          {/* Cloudflare Turnstile Widget */}
          <div className="flex justify-center">
            <Turnstile
              ref={signupTurnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={handleSignupTurnstileSuccess}
              onError={handleTurnstileError}
              onExpire={handleSignupTurnstileExpire}
              options={{
                theme: "dark",
                size: "normal",
              }}
            />
          </div>
          {turnstileError && (
            <p className="text-sm text-destructive text-center">Verification failed. Please try again.</p>
          )}

          <Button
            type="submit"
            className="w-full glass-premium border-premium"
            disabled={isLoading || !signupTurnstileToken}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>
      </TabsContent>
    </Tabs>
  );
}
